// First-person walker: smooth acceleration, fixed eye height, 2D collision. In an outdoor field (manifest
// `outdoor`) the eye follows the ground's height (and the ruins' platforms: steps up and down glide) and the
// position wraps round the field (outdoor/field.ts).

import type { PerspectiveCamera } from 'three';
import type { Manifest } from '../scene/manifest';
import { Collider, type WalkCollider } from './collision';
import { FieldCollider } from '../outdoor/field-collider';
import { FieldFloor, ground, layoutOnce, wrap as wrapField } from '../outdoor/field';
import type { Input } from './input';

const PITCH_LIMIT = (85 * Math.PI) / 180;
/** Arrow Left / Right turn rate, rad/s (x1.5 with Shift). */
const TURN_RATE = 1.9;

export class Player {
  x: number;
  z: number;
  yaw: number;
  pitch = 0;
  vx = 0;
  vz = 0;
  readonly eyeY: number;
  readonly collider: WalkCollider;
  /** Outdoor field: its size (the position wraps into [0, period)) and the avatars' wrap and fade; else null. */
  readonly wrap: { period: number; fadeFrom: number; fadeTo: number } | null = null;
  private readonly baseY: number;
  private readonly eye: number;
  private readonly groundAt: ((x: number, z: number) => number) | null = null;
  /** Outdoors: the floor height the eye follows, gliding over steps (the platforms' edges). */
  private eyeFloor = NaN;
  private walk: number;
  private runSpeed: number;
  private look = { yaw: 0, pitch: 0 };

  constructor(m: Manifest) {
    const p = m.player;
    const f = m.outdoor;
    if (f) {
      const lay = layoutOnce(f);
      this.collider = new FieldCollider(lay.circles, p.radius, f.size_m);
      const floor = new FieldFloor(lay.slabs, ground(f), f.size_m);
      this.groundAt = (x, z) => floor.height(x, z);
      this.wrap = { period: f.size_m, fadeFrom: f.fog_start_m + (f.view_m - f.fog_start_m) * 0.5, fadeTo: f.view_m };
    } else this.collider = new Collider(m.collision.walkable, m.collision.obstacles, p.radius, m.collision.floor_y, p.eye_height);
    this.baseY = m.collision.floor_y;
    this.eye = p.eye_height;
    this.eyeY = m.collision.floor_y + p.eye_height;
    this.walk = p.walk_speed;
    this.runSpeed = p.run_speed;
    this.collider.settle(m.spawn.position[0], m.spawn.position[2]);
    this.x = this.collider.x;
    this.z = this.collider.z;
    this.yaw = (m.spawn.yaw_deg * Math.PI) / 180;
  }

  /** Advance by dt seconds. Returns true if the pose changed. */
  update(dt: number, input: Input): boolean {
    input.consumeLook(this.look);
    let changed = false;
    if (this.look.yaw !== 0 || this.look.pitch !== 0) {
      this.yaw = wrap(this.yaw + this.look.yaw);
      this.pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, this.pitch + this.look.pitch));
      changed = true;
    }
    if (input.turn !== 0) {
      this.yaw = wrap(this.yaw + input.turn * TURN_RATE * (input.run ? 1.5 : 1) * dt);
      changed = true;
    }

    // desired velocity in world XZ; yaw 0 looks down -Z, +yaw turns left
    const speed = input.run ? this.runSpeed : this.walk;
    const s = Math.sin(this.yaw), c = Math.cos(this.yaw);
    const f = input.moveY * speed, r = input.moveX * speed;
    const tx = -s * f + c * r;
    const tz = -c * f - s * r;
    // exponential approach: quick start, softer stop
    const rate = input.moveX !== 0 || input.moveY !== 0 ? 9 : 12;
    const k = 1 - Math.exp(-rate * dt);
    this.vx += (tx - this.vx) * k;
    this.vz += (tz - this.vz) * k;
    if (Math.abs(this.vx) < 1e-4 && Math.abs(this.vz) < 1e-4 && input.moveX === 0 && input.moveY === 0) { this.vx = 0; this.vz = 0; }

    if (this.vx !== 0 || this.vz !== 0) {
      const nx = this.x + this.vx * dt, nz = this.z + this.vz * dt;
      this.collider.resolve(this.x, this.z, nx, nz);
      const mx = this.collider.x - this.x, mz = this.collider.z - this.z;
      if (mx !== 0 || mz !== 0) changed = true;
      // keep velocity consistent with the slide so it does not build up against a wall
      if (dt > 0) {
        const ax = mx / dt, az = mz / dt;
        if (Math.abs(ax) < Math.abs(this.vx)) this.vx = ax;
        if (Math.abs(az) < Math.abs(this.vz)) this.vz = az;
      }
      this.x = this.collider.x;
      this.z = this.collider.z;
      if (this.wrap) {
        this.x = wrapField(this.x, this.wrap.period);
        this.z = wrapField(this.z, this.wrap.period);
      }
    }

    if (this.groundAt) {
      // the eye glides to a new floor height (a step of a platform) in about 0.15 s; a jump of more than 0.6 m
      // (a teleport) is not glided
      const y = this.floorY;
      if (!(Math.abs(y - this.eyeFloor) < 0.6)) this.eyeFloor = y;
      else if (this.eyeFloor !== y) {
        this.eyeFloor += (y - this.eyeFloor) * (1 - Math.exp(-20 * dt));
        if (Math.abs(y - this.eyeFloor) < 5e-4) this.eyeFloor = y;
        changed = true;
      }
    }
    return changed;
  }

  get moving(): boolean {
    return this.vx !== 0 || this.vz !== 0;
  }

  /** The ground under the player (the room's floor height indoors). */
  get floorY(): number {
    return this.groundAt ? this.groundAt(this.x, this.z) : this.baseY;
  }

  apply(camera: PerspectiveCamera): void {
    let y = this.eyeY;
    if (this.groundAt) {
      const f = this.floorY;
      if (!(Math.abs(f - this.eyeFloor) < 0.6)) this.eyeFloor = f;
      y = this.eyeFloor + this.eye;
    }
    camera.position.set(this.x, y, this.z);
    camera.rotation.set(this.pitch, this.yaw, 0, 'YXZ');
    camera.updateMatrixWorld();
  }
}

function wrap(a: number): number {
  const t = Math.PI * 2;
  a %= t;
  return a > Math.PI ? a - t : a < -Math.PI ? a + t : a;
}
